/**
 * Hook for fetching device-specific questions with hybrid loading strategy
 *
 * Loading Priority:
 * 1. First: Try to fetch from sales_device_questions (model-specific, from Sales Templates)
 * 2. Fallback: Try to fetch from questions table (device-type level questions)
 * 3. Final: Return empty array (triggers legacy hardcoded flow in component)
 *
 * This enables gradual migration from hardcoded questions to database-driven ones.
 */

import { useEffect, useState, useRef } from 'react';
import {
  getPublicSalesQuestionsForModel,
  getPublicQuestionsWithOptions,
  type PublicQuestionWithOptions,
} from '../../../lib/publicDataAccess';
import type { DynamicQuestion } from '../types';

/**
 * Get label from multilingual object
 * Supports all app languages with fallback chain: requested → nl → en → first available → empty
 */
function getLabel(obj: Record<string, string> | null | undefined, language: string): string {
  if (!obj) return '';
  // Try requested language, then Dutch, then English, then first available key
  return obj[language] || obj.nl || obj.en || Object.values(obj)[0] || '';
}

/**
 * Map PublicQuestionWithOptions to DynamicQuestion format
 * Preserves raw labels (labelRaw) for backend/calendar use while extracting current language for display
 */
function mapToQuestions(
  qs: PublicQuestionWithOptions[],
  language: string
): DynamicQuestion[] {
  return qs.map((q) => ({
    id: q.id,
    questionKey: q.questionKey,
    label: getLabel(q.label, language),
    labelRaw: q.label || {},  // Preserve raw JSONB with all translations
    subtitle: getLabel(q.subtitle, language) || null,
    helpText: getLabel(q.helpText, language) || null,
    isRequired: q.isRequired,
    orderIndex: q.orderIndex,
    options: q.options.map((opt) => ({
      id: opt.id,
      optionKey: opt.optionKey,
      label: getLabel(opt.label, language),
      labelRaw: opt.label || {},  // Preserve raw JSONB with all translations
      description: getLabel(opt.description, language) || null,
      descriptionRaw: opt.description || null,  // Preserve raw JSONB for language switches
      iconName: opt.iconName || null,
      priceEffectType: opt.priceEffectType,
      priceEffectValue: opt.priceEffectValue ?? null,
    })),
  }));
}

export type QuestionSource = 'model' | 'type' | 'legacy' | 'none';

interface UseDeviceQuestionsResult {
  questions: DynamicQuestion[];
  questionsLoading: boolean;
  questionsError: string | null;
  /** Where the questions came from: 'model' (sales_device_questions), 'type' (questions table), 'legacy' (hardcoded), 'none' */
  questionSource: QuestionSource;
  setQuestions: React.Dispatch<React.SetStateAction<DynamicQuestion[]>>;
}

/**
 * Hook to fetch questions for a device with hybrid loading strategy
 *
 * @param modelKey - Device model key (e.g., "macbook-air-m1-2020", "iphone-15-pro")
 * @param deviceTypeKey - Device type key (e.g., "iphone", "macbook") for fallback
 * @param language - Current language (any supported language code)
 * @returns Object with questions, loading state, error state, and question source
 */
export function useDeviceQuestions(
  modelKey: string,
  deviceTypeKey: string,
  language: string
): UseDeviceQuestionsResult {
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [questionSource, setQuestionSource] = useState<QuestionSource>('none');

  // ── Synchronous reset when keys change ──
  // This runs DURING render (before JSX evaluates), so the component
  // never sees stale questions paired with a new modelKey.
  // React docs: "Storing information from previous renders"
  const [prevKeys, setPrevKeys] = useState({ modelKey, deviceTypeKey });
  if (prevKeys.modelKey !== modelKey || prevKeys.deviceTypeKey !== deviceTypeKey) {
    setPrevKeys({ modelKey, deviceTypeKey });
    setQuestions([]);
    setQuestionsLoading(!!(modelKey || deviceTypeKey)); // true if we'll fetch, false if empty
    setQuestionsError(null);
    setQuestionSource('none');
  }

  // Track current request to handle race conditions
  const requestIdRef = useRef(0);

  useEffect(() => {
    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current;

    // If no keys provided, reset state
    if (!modelKey && !deviceTypeKey) {
      setQuestions([]);
      setQuestionsLoading(false);
      setQuestionsError(null);
      setQuestionSource('none');
      return;
    }

    let isCancelled = false;
    // Questions & loading already reset synchronously above;
    // just ensure loading is true in case only language changed.
    setQuestionsLoading(true);
    setQuestionsError(null);

    (async () => {
      try {
        // ========================================
        // Step 1: Try model-specific sales questions
        // ========================================
        if (modelKey) {
          console.log(`[useDeviceQuestions] Fetching sales questions for model: "${modelKey}"`);
          const modelQuestions = await getPublicSalesQuestionsForModel(modelKey);

          // Check if this request is still current
          if (isCancelled || currentRequestId !== requestIdRef.current) {
            console.log('[useDeviceQuestions] Request cancelled (model phase)');
            return;
          }

          if (modelQuestions.length > 0) {
            console.log(`[useDeviceQuestions] Using ${modelQuestions.length} model-specific questions`);
            const mapped = mapToQuestions(modelQuestions, language);
            setQuestions(mapped);
            setQuestionSource('model');
            setQuestionsLoading(false);
            return;
          }
        }

        // ========================================
        // Step 2: Fallback to device-type questions
        // ========================================
        if (deviceTypeKey) {
          console.log(`[useDeviceQuestions] No model questions, falling back to device type: "${deviceTypeKey}"`);
          const typeQuestions = await getPublicQuestionsWithOptions(deviceTypeKey);

          // Check if this request is still current
          if (isCancelled || currentRequestId !== requestIdRef.current) {
            console.log('[useDeviceQuestions] Request cancelled (type phase)');
            return;
          }

          if (typeQuestions.length > 0) {
            console.log(`[useDeviceQuestions] Using ${typeQuestions.length} device-type questions`);
            const mapped = mapToQuestions(typeQuestions, language);
            setQuestions(mapped);
            setQuestionSource('type');
            setQuestionsLoading(false);
            return;
          }
        }

        // ========================================
        // Step 3: No DB questions found - use legacy flow
        // ========================================
        console.log('[useDeviceQuestions] No DB questions found, component should use legacy hardcoded flow');
        setQuestions([]);
        setQuestionSource('legacy');

      } catch (err: any) {
        // Check if this request is still current
        if (isCancelled || currentRequestId !== requestIdRef.current) {
          return;
        }

        console.error('[useDeviceQuestions] Error loading questions:', err);
        setQuestions([]);
        setQuestionsError('Failed to load questions. Please try again.');
        setQuestionSource('none');
      } finally {
        // Only update loading state if this request is still current
        if (!isCancelled && currentRequestId === requestIdRef.current) {
          setQuestionsLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [modelKey, deviceTypeKey, language]);

  return {
    questions,
    questionsLoading,
    questionsError,
    questionSource,
    setQuestions,
  };
}
